import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BiChevronDown, BiChevronRight, BiFolder, BiFile, BiGridAlt, BiGridVertical, BiMoveHorizontal,
} from 'react-icons/bi';
import { useBuilderEditor } from '../../features/builder/BuilderEditorContext';

// Hoisted to module scope so these option objects keep a stable identity
// across every render. dnd-kit's useSensor/useSensors memoize on the options
// reference; a fresh object literal each render (the previous inline form)
// defeats that memoization and can tear down/rebuild the pointer sensor's
// listeners mid-gesture, which is what made the first drag attempt after a
// re-render occasionally fail to activate.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 6 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

const SortableFieldItem = ({ id, data, disabled, children }) => {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id, data, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`builder-tree__sortable ${isDragging ? 'builder-tree__sortable--dragging' : ''}`}
    >
      {children({ attributes, listeners, isDragging })}
    </div>
  );
};

const SortableSectionRow = ({ id, data, disabled, isDropTarget, children }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id, data, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`builder-tree__section-row builder-tree__sortable ${isDragging ? 'builder-tree__sortable--dragging' : ''} ${isDropTarget ? 'builder-tree__section-row--drop-target' : ''}`}
      {...attributes}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="builder-tree__drag"
        aria-label="Drag to reorder section"
        {...listeners}
      >
        <BiGridVertical aria-hidden="true" />
      </button>
      {children}
    </div>
  );
};

const TreeNavigation = ({
  selectedSectionId,
  selectedFieldId,
  onSelectSection,
  onSelectField,
  onSelectRootFields,
  lastCreatedItem,
  lastMovedItem,
}) => {
  const {
    formdata, formsectionsdata, currentsection, searchQuery, setSearchQuery,
    viewSection, setIsSubsection, handleFieldDrop, handleSectionDrop,
    openMoveSectionModal, moveSectionToParent,
  } = useBuilderEditor();
  const [expanded, setExpanded] = useState({ 'root-fields': true });

  const isDragEnabled = !searchQuery;

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: prev[key] === false }));
  };

  const findParentSectionId = (sectionId, sections) => {
    for (const section of sections || []) {
      if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
      const parentId = findParentSectionId(sectionId, section.subSections);
      if (parentId != null) return parentId;
    }
    return null;
  };

  const getAncestorChainIds = (sectionId, sections) => {
    const ids = [];
    let currentId = sectionId;
    while (currentId != null) {
      ids.push(currentId);
      currentId = findParentSectionId(currentId, sections);
    }
    return ids;
  };

  // When a new Section/Subsection/Question is created, make sure the sidebar
  // reveals it: expand the containing section (and its ancestor chain) even if
  // the user had previously collapsed it. Keyed on the lastCreatedItem object
  // reference (a fresh object every creation) so repeated creates in the same
  // section still re-expand it if it was collapsed again in between.
  const revealSectionChain = (targetId) => {
    if (targetId == null) return;
    if (targetId === 0) {
      setExpanded((prev) => ({ ...prev, 'root-fields': true }));
      return;
    }
    const chain = getAncestorChainIds(targetId, formdata?.sections);
    setExpanded((prev) => {
      const next = { ...prev };
      chain.forEach((id) => { next[`section-${id}`] = true; });
      return next;
    });
  };

  useEffect(() => {
    if (!lastCreatedItem) return;
    const targetId = lastCreatedItem.type === 'section'
      ? (lastCreatedItem.parentSectionId > 0 ? lastCreatedItem.parentSectionId : null)
      : lastCreatedItem.sectionId;
    revealSectionChain(targetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCreatedItem]);

  // When a drag-and-drop move relocates a Section/Subsection/Question, reveal the
  // destination the same way a newly-created item is revealed above.
  useEffect(() => {
    if (!lastMovedItem) return;
    if (lastMovedItem.type === 'section') {
      if (lastMovedItem.parentSectionId > 0) revealSectionChain(lastMovedItem.parentSectionId);
      return;
    }
    revealSectionChain(lastMovedItem.sectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMovedItem]);

  const matchesSearch = (text) => {
    if (!searchQuery) return true;
    return text?.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS)
  );

  // Auto-expand a collapsed Section/Subsection after the dragged item hovers
  // over its row for a short beat, so it can be dropped into (not just onto)
  // a currently-collapsed target.
  const hoverExpandTimeoutRef = useRef(null);
  const hoverExpandKeyRef = useRef(null);

  const clearHoverExpand = () => {
    if (hoverExpandTimeoutRef.current) {
      clearTimeout(hoverExpandTimeoutRef.current);
      hoverExpandTimeoutRef.current = null;
    }
    hoverExpandKeyRef.current = null;
  };

  useEffect(() => () => clearHoverExpand(), []);

  // Purely visual drag feedback — a floating preview of the item being
  // dragged (activeDragItem) and an explicit "it will land here" indicator
  // (dropIndicator: which row/container is currently the drop target, and
  // whether the insertion point is before/after it or appended at the end).
  // Neither of these feeds into handleDragEnd's actual move logic below —
  // they're display-only state computed from the same event data.
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);

  const resetDragVisuals = () => {
    clearHoverExpand();
    setActiveDragItem(null);
    setDropIndicator(null);
  };

  const handleDragStart = (event) => {
    const data = event.active.data.current;
    if (!data) return;
    if (data.type === 'field') {
      const fields = getFieldsForSection(data.sectionId);
      const field = fields.find((f) => f.id === data.fieldId);
      setActiveDragItem({ type: 'field', label: field?.name || 'Untitled' });
    } else if (data.type === 'section') {
      const section = findSectionById(data.sectionId);
      setActiveDragItem({ type: 'section', label: section?.title || 'Untitled section' });
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    const activeData = active?.data?.current;
    const overData = over?.data?.current;

    if (!overData || overData.type !== 'section') {
      clearHoverExpand();
    } else {
      const key = `section-${overData.sectionId}`;
      if (hoverExpandKeyRef.current !== key) {
        clearHoverExpand();
        hoverExpandKeyRef.current = key;
        hoverExpandTimeoutRef.current = setTimeout(() => {
          setExpanded((prev) => (prev[key] === false ? { ...prev, [key]: true } : prev));
        }, 500);
      }
    }

    if (!activeData || !overData || !over || active.id === over.id) {
      setDropIndicator(null);
      return;
    }

    if (activeData.type === 'field') {
      if (overData.type === 'field') {
        // Same-container reorders decide before/after from index direction
        // (handleDragEnd's getDropTargetField); cross-container drops have no
        // "previous index" to compare, so they fall back to pointer geometry
        // (also what handleDragEnd itself uses for that case). Whichever rule
        // handleDragEnd will actually use is the one the indicator must show.
        let before;
        if (activeData.sectionId === overData.sectionId) {
          const sourceFields = getFieldsForSection(activeData.sectionId);
          const activeIndex = sourceFields.findIndex((f) => f.id === activeData.fieldId);
          const overIndex = sourceFields.findIndex((f) => f.id === overData.fieldId);
          before = !(activeIndex < overIndex);
        } else {
          before = isDropBeforeTarget(event);
        }
        setDropIndicator({
          kind: 'field', sectionId: overData.sectionId, id: overData.fieldId, position: before ? 'before' : 'after',
        });
      } else if (overData.type === 'section') {
        setDropIndicator({ kind: 'section-end', sectionId: overData.sectionId });
      }
      return;
    }

    if (activeData.type === 'section' && overData.type === 'section') {
      let before;
      if (activeData.parentSectionId === overData.parentSectionId) {
        const list = activeData.parentSectionId
          ? findSectionById(activeData.parentSectionId)?.subSections
          : formdata?.sections;
        const activeIndex = list?.findIndex((s) => s.id === activeData.sectionId) ?? -1;
        const overIndex = list?.findIndex((s) => s.id === overData.sectionId) ?? -1;
        before = !(activeIndex < overIndex);
      } else {
        before = isDropBeforeTarget(event);
      }
      setDropIndicator({
        kind: 'section', containerKey: overData.containerKey, id: overData.sectionId, position: before ? 'before' : 'after',
      });
    }
  };

  const findSectionById = (sectionId, sections = formdata?.sections) => {
    for (const section of sections || []) {
      if (section.id === sectionId) return section;
      const sub = findSectionById(sectionId, section.subSections);
      if (sub) return sub;
    }
    return null;
  };

  const getFieldsForSection = (sectionId, fallbackFields = []) => {
    if (sectionId === 0) return formdata?.fields || [];
    if (formsectionsdata?.id === sectionId && formsectionsdata?.fields?.length) {
      return formsectionsdata.fields;
    }
    if (currentsection === sectionId && formsectionsdata?.fields?.length) {
      return formsectionsdata.fields;
    }
    const section = findSectionById(sectionId);
    return section?.fields?.length ? section.fields : fallbackFields;
  };

  const sectionMatchesSearch = (section) => {
    if (matchesSearch(section.title)) return true;
    const fields = getFieldsForSection(section.id, section.fields);
    if (fields?.some((f) => matchesSearch(f.name))) return true;
    return section.subSections?.some((s) => sectionMatchesSearch(s));
  };

  const getDropTargetField = (fields, activeIndex, overIndex) => {
    if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) return null;
    if (activeIndex < overIndex) {
      return fields[overIndex];
    }
    if (overIndex === 0) {
      return { id: 0 };
    }
    return fields[overIndex - 1];
  };

  // For cross-container drops (a different Section/Subsection than the one the
  // dragged item started in) there's no "previous index" to compare against —
  // so instead compare the dragged item's current on-screen position to the
  // hovered row's vertical midpoint, the same "which half of the row am I
  // over" signal same-container reordering gets from index direction. Keeps
  // cross-container drops feeling as direction-aware as same-container ones.
  const isDropBeforeTarget = ({ active, over }) => {
    const activeRect = active?.rect?.current?.translated || active?.rect?.current?.initial;
    if (!activeRect || !over?.rect) return true;
    const activeCenterY = activeRect.top + activeRect.height / 2;
    const overCenterY = over.rect.top + over.rect.height / 2;
    return activeCenterY < overCenterY;
  };

  const handleDragEnd = (event) => {
    resetDragVisuals();
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;
    if (!activeData || !overData) return;

    if (activeData.type === 'field') {
      if (overData.type !== 'field' && overData.type !== 'section') return;

      const sourceFields = getFieldsForSection(activeData.sectionId);
      const droppedField = sourceFields.find((f) => f.id === activeData.fieldId);
      if (!droppedField) return;

      if (overData.type === 'field' && activeData.sectionId === overData.sectionId) {
        // Reorder within the same Section/Subsection (unchanged existing behavior).
        const activeIndex = sourceFields.findIndex((f) => f.id === activeData.fieldId);
        const overIndex = sourceFields.findIndex((f) => f.id === overData.fieldId);
        const droppedOver = getDropTargetField(sourceFields, activeIndex, overIndex);
        if (droppedOver) handleFieldDrop(droppedField, droppedOver, activeData.sectionId);
        return;
      }

      if (overData.type === 'field') {
        // Cross-container: dropped next to a Question in a different Section/Subsection.
        const destFields = getFieldsForSection(overData.sectionId);
        const overIndex = destFields.findIndex((f) => f.id === overData.fieldId);
        const anchorIndex = isDropBeforeTarget(event) ? overIndex - 1 : overIndex;
        const droppedOver = anchorIndex < 0 ? { id: 0 } : destFields[anchorIndex];
        handleFieldDrop(droppedField, droppedOver, overData.sectionId);
        return;
      }

      // Dropped directly on a Section/Subsection row (not on a specific Question):
      // append to the end of that Section's/Subsection's list.
      const destFields = getFieldsForSection(overData.sectionId);
      const droppedOver = destFields.length ? destFields[destFields.length - 1] : { id: 0 };
      if (droppedOver.id === droppedField.id) return;
      handleFieldDrop(droppedField, droppedOver, overData.sectionId);
      return;
    }

    if (activeData.type === 'section' && overData.type === 'section') {
      if (activeData.parentSectionId === overData.parentSectionId) {
        // Reorder within the same parent (top-level Sections, or Subsections of the same Section).
        if (activeData.containerKey !== overData.containerKey) return;
        const list = activeData.parentSectionId
          ? findSectionById(activeData.parentSectionId)?.subSections
          : formdata?.sections;
        const droppedSection = list?.find((s) => s.id === activeData.sectionId);
        const droppedOver = list?.find((s) => s.id === overData.sectionId);
        if (droppedSection && droppedOver) {
          handleSectionDrop(droppedSection, droppedOver, activeData.parentSectionId || 0);
        }
        return;
      }

      // Cross-parent move: only Subsections re-parent via drag; top-level Sections
      // keep reordering-only behavior (guarded by the equality check above).
      if (!activeData.parentSectionId) return;
      const sourceParent = findSectionById(activeData.parentSectionId);
      const droppedSection = sourceParent?.subSections?.find((s) => s.id === activeData.sectionId);
      if (!droppedSection) return;

      let targetParentId;
      let prevSectionId = 0;
      if (!overData.parentSectionId) {
        // Hovering a top-level Section's own row: becomes its first Subsection.
        targetParentId = overData.sectionId;
      } else {
        // Hovering another Subsection's row: insert before or after it among its
        // siblings depending on which half of the row is being hovered.
        targetParentId = overData.parentSectionId;
        const targetParent = findSectionById(targetParentId);
        const siblings = targetParent?.subSections || [];
        const siblingIdx = siblings.findIndex((s) => s.id === overData.sectionId);
        const anchorIdx = isDropBeforeTarget(event) ? siblingIdx - 1 : siblingIdx;
        prevSectionId = anchorIdx >= 0 ? siblings[anchorIdx].id : 0;
      }

      moveSectionToParent(droppedSection, activeData.parentSectionId, targetParentId, prevSectionId);
    }
  };

  const renderFields = (sectionId, fallbackFields, depth = 0) => {
    const fields = getFieldsForSection(sectionId, fallbackFields);
    if (!fields?.length) return null;

    const filtered = fields.filter((f) => matchesSearch(f.name) && !f.hide_alt);
    if (!filtered.length) return null;

    const containerKey = `fields-${sectionId}`;
    const ids = filtered.map((f) => `field-${f.id}`);

    return (
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {filtered.map((field) => {
          const isFieldDropTarget = dropIndicator?.kind === 'field'
            && dropIndicator.sectionId === sectionId && dropIndicator.id === field.id;
          return (
            <React.Fragment key={field.id}>
              {isFieldDropTarget && dropIndicator.position === 'before' && (
                <div className="builder-tree__drop-line" />
              )}
              <SortableFieldItem
                id={`field-${field.id}`}
                disabled={!isDragEnabled}
                data={{
                  type: 'field',
                  containerKey,
                  sectionId,
                  fieldId: field.id,
                }}
              >
                {({ attributes, listeners, isDragging }) => (
                  <div
                    role="treeitem"
                    aria-grabbed={isDragging}
                    aria-selected={selectedFieldId === field.id}
                    className={`builder-tree__item builder-tree__item--field builder-tree__item--nested${depth > 0 ? '-2' : ''} ${selectedFieldId === field.id ? 'builder-tree__item--selected' : ''}`}
                    {...attributes}
                    {...listeners}
                    onClick={() => onSelectField(field.id, sectionId)}
                  >
                    <BiGridVertical className="builder-tree__drag-icon" aria-hidden="true" />
                    <BiFile className="builder-tree__icon" aria-hidden="true" />
                    <span className="builder-tree__label">{field.name || 'Untitled'}</span>
                  </div>
                )}
              </SortableFieldItem>
              {isFieldDropTarget && dropIndicator.position === 'after' && (
                <div className="builder-tree__drop-line" />
              )}
            </React.Fragment>
          );
        })}
        {dropIndicator?.kind === 'section-end' && dropIndicator.sectionId === sectionId && (
          <div className="builder-tree__drop-line builder-tree__drop-line--end" />
        )}
      </SortableContext>
    );
  };

  const renderSectionList = (sections, parentSectionId = 0, depth = 0) => {
    if (!sections?.length) return null;
    const visible = sections.filter((s) => !s.hide_alt && sectionMatchesSearch(s));
    if (!visible.length) return null;

    const containerKey = `sections-${parentSectionId}`;
    const ids = visible.map((s) => `section-${s.id}`);

    return (
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {visible.map((section) => {
          const isSectionDropTarget = dropIndicator?.kind === 'section'
            && dropIndicator.containerKey === containerKey && dropIndicator.id === section.id;
          return (
            <React.Fragment key={section.id}>
              {isSectionDropTarget && dropIndicator.position === 'before' && (
                <div className="builder-tree__drop-line" />
              )}
              {renderSection(section, depth, parentSectionId, containerKey)}
              {isSectionDropTarget && dropIndicator.position === 'after' && (
                <div className="builder-tree__drop-line" />
              )}
            </React.Fragment>
          );
        })}
      </SortableContext>
    );
  };

  const renderSection = (section, depth = 0, parentSectionId = 0, containerKey = `sections-${parentSectionId}`) => {
    const key = `section-${section.id}`;
    const isExpanded = expanded[key] !== false;
    const isSelected = selectedSectionId === section.id && !selectedFieldId;
    const sectionFields = getFieldsForSection(section.id, section.fields);
    const fieldCount = (sectionFields?.length || 0)
      + (section.subSections?.reduce((acc, s) => acc + (getFieldsForSection(s.id, s.fields)?.length || 0), 0) || 0);
    // A Section/Subsection is "receiving" a dragged Question either when it's
    // hovered directly (append-to-end) or when one of its own Questions is
    // the current insertion anchor — either way, highlight it as the
    // destination container so cross-Section/Subsection moves are unambiguous.
    const isFieldDropTarget = (dropIndicator?.kind === 'section-end' || dropIndicator?.kind === 'field')
      && dropIndicator.sectionId === section.id;
    const moveBlockedBySubsections = depth === 0 && section.subSections?.length > 0;

    return (
      <div key={section.id} role="group">
        <SortableSectionRow
          id={`section-${section.id}`}
          disabled={!isDragEnabled}
          isDropTarget={isFieldDropTarget}
          data={{
            type: 'section',
            containerKey,
            sectionId: section.id,
            parentSectionId,
          }}
        >
          <div
            role="treeitem"
            aria-expanded={isExpanded}
            aria-selected={isSelected}
            className={`builder-tree__item ${depth > 0 ? 'builder-tree__item--nested' : ''} ${isSelected ? 'builder-tree__item--selected' : ''}`}
            onClick={() => {
              setIsSubsection(depth > 0);
              onSelectSection(section.id);
              viewSection(section.id);
            }}
          >
            <button
              type="button"
              className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon 1"
              style={{ width: 24, height: 24, padding: 0 }}
              onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
            </button>
            <BiFolder className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{section.title}</span>
            {/* <span className="builder-tree__count">{fieldCount}</span> */}
            <button
              type="button"
              className="builder-tree__action"
              aria-label="Move to section"
              title={moveBlockedBySubsections ? 'Cannot move: this section contains subsections' : 'Move to'}
              disabled={moveBlockedBySubsections}
              style={moveBlockedBySubsections ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              onClick={(e) => {
                e.stopPropagation();
                openMoveSectionModal(section, parentSectionId);
              }}
            >
              <BiMoveHorizontal aria-hidden="true" />
            </button>
          </div>
        </SortableSectionRow>
        {isExpanded && (
          <>
            {renderFields(section.id, section.fields, depth)}
            {renderSectionList(section.subSections, section.id, depth + 1)}
          </>
        )}
      </div>
    );
  };

  const renderRootFields = () => {
    if (!formdata?.fields?.length) return null;
    const key = 'root-fields';
    const isExpanded = expanded[key] !== false;
    const isSelected = selectedSectionId === 0 && !selectedFieldId;
    const visibleCount = formdata.fields.filter((f) => matchesSearch(f.name)).length;

    return (
      <div role="group">
        <div
          role="treeitem"
          aria-expanded={isExpanded}
          aria-selected={isSelected}
          className={`builder-tree__item ${isSelected ? 'builder-tree__item--selected' : ''}`}
          onClick={onSelectRootFields}
        >
          <button
            type="button"
            className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon 2"
            style={{ width: 24, height: 24, padding: 0 }}
            onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
          </button>
          <BiGridAlt className="builder-tree__icon" />
          <span className="builder-tree__label">Root Questions</span>
          {/* <span className="builder-tree__count">{formdata.fields.length}</span> */}
        </div>
        {isExpanded && visibleCount > 0 && renderFields(0, formdata.fields, 0)}
      </div>
    );
  };

  if (!formdata) {
    return (
      <div className="builder-tree">
        <div className="builder-tree__header">
          <div className="builder-tree__search">
            <input disabled placeholder="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <nav className="builder-tree" aria-label="Form structure">
      <div className="builder-tree__header">
        <div className="builder-tree__search">
          <input
            type="search"
            placeholder="Filter tree…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Filter navigation tree"
          />
        </div>
        {searchQuery && (
          <p className="builder-tree__hint">Clear search to reorder items</p>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        autoScroll={{ threshold: { x: 0.2, y: 0.25 }, acceleration: 10, interval: 5 }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={resetDragVisuals}
      >
        <div className="builder-tree__body" role="tree">
          {renderRootFields()}
          {renderSectionList(formdata.sections, 0)}
        </div>
        <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
          {activeDragItem ? (
            <div className={`builder-tree__drag-preview builder-tree__drag-preview--${activeDragItem.type}`}>
              {activeDragItem.type === 'section' ? <BiFolder aria-hidden="true" /> : <BiFile aria-hidden="true" />}
              <span>{activeDragItem.label}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </nav>
  );
};

export default TreeNavigation;
